import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Skeleton } from '@mui/material';
import { cn } from 'cn';

import { type UncefactUnitType } from '~/types/unit';

import { getAbbreviatedUnit } from '~/utils/unit';

const LegendItem = ({
  amount,
  color,
  label,
  renderSkeletonView,
  selectedUnit,
}: {
  readonly amount: number;
  readonly color: string;
  readonly label: string;
  readonly renderSkeletonView: boolean;
  readonly selectedUnit?: UncefactUnitType;
}) => (
  <div className="flex w-full items-center gap-1.5">
    {renderSkeletonView ? (
      <Skeleton className="h-7 w-1.5 flex-none" variant="rectangular" />
    ) : (
      <div className="h-7 w-1.5 flex-none" style={{ backgroundColor: color }} />
    )}
    <div className="min-w-0 flex-1 text-start text-xs leading-tight">
      {renderSkeletonView ? (
        <Skeleton className="w-[12ch]" variant="text" />
      ) : (
        <div className="truncate">{label}</div>
      )}
      <div className="flex items-center">
        {renderSkeletonView ? (
          <>
            <Skeleton className="inline-block w-[5ch]" variant="text" />
            <Skeleton
              className="ml-[0.5ch] inline-block w-[3ch]"
              variant="text"
            />
          </>
        ) : (
          <>
            <span>{amount}</span>
            <span className="ml-[0.5ch] text-gray-500">
              {getAbbreviatedUnit(selectedUnit)}
            </span>
          </>
        )}
      </div>
    </div>
  </div>
);

type P = {
  readonly data: Array<{
    amount: number;
    color: string;
    label: string;
  }>;
  readonly renderSkeletonView: boolean;
  readonly selectedUnit?: UncefactUnitType;
} & ComponentStyling;

export const ChartLegend = ({
  className,
  data,
  renderSkeletonView,
  selectedUnit,
  style,
}: P) => {
  const [animationRef] = useAutoAnimate({
    duration: 100,
  });

  if (!(data?.length > 0)) {
    return null;
  }

  return (
    <div
      ref={animationRef}
      className={cn('flex w-full flex-col gap-1', className)}
      style={style}
    >
      {data.map(({ amount, color, label }) => (
        <LegendItem
          key={label}
          amount={amount}
          color={color}
          label={label}
          renderSkeletonView={renderSkeletonView}
          selectedUnit={selectedUnit}
        />
      ))}
    </div>
  );
};
